import { type CalendarRange, calendarTypeToUnitName, UnitConversionError, UnitsTracker } from './units.ts';

export type Expression =
  | Num
  | Str
  | Bool
  | CalendarValue
  | Identifier
  | AccessField
  | Negate
  | Convert
  | Binary
  | Call
  | Group;

// We are intentionally not extending Error as we don't care about the stack trace:
export class SimpleEvalError {
  readonly message: string;
  readonly details: { type: 'missingVariable'; nameWithAnnotation: string } | null;
  constructor(message: string, details: SimpleEvalError['details'] | null = null) {
    this.message = message;
    this.details = details;
  }
}

export function astWrapPrimitiveValue(
  val: number | string | boolean | CalendarRange,
  units: UnitsTracker | null,
): Num | Str | Bool | CalendarValue {
  if (typeof val === 'number') {
    return new Num(val, units ?? UnitsTracker.unitless());
  } else if (typeof val === 'string') {
    return new Str(val);
  } else if (typeof val === 'boolean') {
    return Bool.from(val);
  } else if (typeof val === 'object' && 'calendarType' in val) {
    return new CalendarValue(val);
  } else {
    throw new Error(`Cannot wrap value of type ${typeof val} in AST`);
  }
}

export class Num {
  public readonly type = 'Num';
  public readonly value: number;
  public readonly units: UnitsTracker;

  constructor(value: number, units: UnitsTracker) {
    this.value = value;
    this.units = units;
    const newValueOrFalse = units.convertToScalarIfPercentage(value);
    if (newValueOrFalse !== false) {
      this.value = newValueOrFalse;
      this.units = UnitsTracker.unitless();
    }
  }

  accept<R>(visitor: ExpressionVisitor<R>): R {
    return visitor.visitNum(this);
  }

  negate(): Num {
    return new Num(-this.value, this.units);
  }

  add(other: Num): Num | SimpleEvalError {
    if (!this.units.canAddOrSubtract(other.units)) {
      return new SimpleEvalError(
        `Cannot add ${this.value} ${this.units.toString()} to ${other.value} ${other.units.toString()}`,
      );
    }

    return new Num(this.value + other.value, this.units);
  }

  subtract(other: Num): Num | SimpleEvalError {
    if (!this.units.canAddOrSubtract(other.units)) {
      return new SimpleEvalError(
        `Cannot subtract ${other.value} ${other.units.toString()} from ${this.value} ${this.units.toString()} `,
      );
    }

    return new Num(this.value - other.value, this.units);
  }

  multiply(other: Num): Num {
    return new Num(this.value * other.value, this.units.multiply(other.units));
  }

  divide(other: Num): Num {
    return new Num(this.value / other.value, this.units.divide(other.units));
  }

  power(other: Num): Num | SimpleEvalError {
    const units = this.units.power(other.units, other.value);
    if (units instanceof UnitConversionError) {
      return new SimpleEvalError(units.message);
    }

    return new Num(this.value ** other.value, units);
  }

  toString() {
    const numStr = String(this.value);
    const unitsStr = this.units.toString();
    if (unitsStr) {
      return numStr + ' ' + unitsStr;
    } else {
      return numStr;
    }
  }
}

export class CalendarValue {
  public readonly type = 'CalendarValue';
  public readonly value: CalendarRange;

  constructor(value: CalendarRange) {
    this.value = value;
  }

  accept<R>(visitor: ExpressionVisitor<R>): R {
    return visitor.visitCalendarValue(this);
  }

  // TODO: 3. Emissions - Reports - Likely need add/subtract support, though it will need to be restricted
  //  (i.e. 2024 calyear + 1 week is nonsensical.)

  toString() {
    return `'${this.value.key}' ${calendarTypeToUnitName[this.value.calendarType]}`;
  }
}

export class Str {
  public readonly type = 'Str';
  public readonly value: string;

  constructor(value: string) {
    this.value = value;
  }

  accept<R>(visitor: ExpressionVisitor<R>): R {
    return visitor.visitStr(this);
  }

  add(other: Str | Num): Str {
    return new Str(String(other) + String(this));
  }

  toString() {
    return this.value;
  }
}

export class Bool {
  static truthy = new Bool(true);
  static falsy = new Bool(false);
  static from(value: boolean) {
    return value ? Bool.truthy : Bool.falsy;
  }

  public readonly type = 'Bool';
  public readonly value: boolean;

  private constructor(value: boolean) {
    this.value = value;
  }

  accept<R>(visitor: ExpressionVisitor<R>): R {
    return visitor.visitBool(this);
  }

  toString() {
    return String(this.value);
  }
}

export class Identifier {
  public readonly type = 'Identifier';
  public readonly nameBare: string;
  public readonly annotation: string;
  public readonly nameWithAnnotation: string;

  constructor(nameBare: string, annotation: string) {
    this.nameBare = nameBare;
    this.annotation = annotation;
    if (this.annotation) {
      this.nameWithAnnotation = this.nameBare + '[' + this.annotation + ']';
    } else {
      this.nameWithAnnotation = this.nameBare;
    }
  }

  accept<R>(visitor: ExpressionVisitor<R>): R {
    return visitor.visitIdentifier(this);
  }

  toString() {
    return this.nameWithAnnotation;
  }
}

export class AccessField {
  public readonly type = 'AccessField';
  public readonly expr: Expression;
  public readonly name: Identifier;

  constructor(expr: Expression, name: Identifier) {
    this.expr = expr;
    this.name = name;
  }
  accept<R>(visitor: ExpressionVisitor<R>): R {
    return visitor.visitAccessField(this);
  }
}

export class Negate {
  public readonly type = 'Negate';
  public readonly expr: Expression;

  constructor(expr: Expression) {
    this.expr = expr;
  }
  accept<R>(visitor: ExpressionVisitor<R>): R {
    return visitor.visitNegate(this);
  }
}

export type BinaryOperator = '+' | '-' | '*' | '/' | '^' | '=' | '!=' | '<' | '>' | '<=' | '>=';
export class Binary {
  public readonly type = 'Binary';
  public readonly left: Expression;
  public readonly operator: BinaryOperator;
  public readonly right: Expression;

  constructor(left: Expression, operator: BinaryOperator, right: Expression) {
    this.left = left;
    this.operator = operator;
    this.right = right;
  }
  accept<R>(visitor: ExpressionVisitor<R>): R {
    return visitor.visitBinary(this);
  }
}

export class Convert {
  public readonly type = 'Convert';
  public readonly expr: Expression;
  public readonly units: UnitsTracker;

  constructor(expr: Expression, units: UnitsTracker) {
    this.expr = expr;
    this.units = units;
  }
  accept<R>(visitor: ExpressionVisitor<R>): R {
    return visitor.visitConvert(this);
  }
}

export class Call {
  public readonly type = 'Call';
  public readonly callee: Expression;
  public readonly args: Expression[];

  constructor(callee: Expression, args: Expression[]) {
    this.callee = callee;
    this.args = args;
  }
  accept<R>(visitor: ExpressionVisitor<R>): R {
    return visitor.visitCall(this);
  }
}

export class Group {
  public readonly type = 'Group';
  public readonly expr: Expression;

  constructor(expr: Expression) {
    this.expr = expr;
  }
  accept<R>(visitor: ExpressionVisitor<R>): R {
    return visitor.visitGroup(this);
  }
}

export type ExpressionVisitor<T> = {
  [Expr in Expression as `visit${Expr['type']}`]: (visit: Expr) => T;
};
